import { useState } from "react";
import { BellRing, Send } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { getReminder, saveReminder } from "@/lib/reminder";
import { enablePush, disablePush, updateReminderTime } from "@/lib/push";

const MESSAGES = {
  ok: "Rappel activé, même application fermée",
  denied: "Notifications refusées : rappel seulement quand l'application est ouverte",
  unsupported: "Ce navigateur ne gère pas les notifications : rappel seulement dans l'application",
};

export function ReminderSettings() {
  const [r, setR] = useState(getReminder);
  const [busy, setBusy] = useState(false);
  const save = (patch) => {
    const next = { ...r, ...patch, lastShown: undefined };
    setR(next);
    saveReminder(next);
  };

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      if (enabled) {
        const res = await enablePush(r.time);
        save({ enabled: true, push: res === "ok" });
        toast[res === "ok" ? "success" : "warning"](MESSAGES[res]);
      } else {
        await disablePush();
        save({ enabled: false, push: false });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const changeTime = (time) => {
    if (!time) return;
    save({ time });
    updateReminderTime(time).catch(() => {});
  };

  const test = async () => {
    try {
      await api.post("/push/test");
      toast.success("Notification envoyée");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 p-4 space-y-3 rise rise-1" data-testid="reminder-settings">
      <div className="flex items-center gap-3">
        <span className="w-12 h-12 rounded-xl bg-slate-900 text-amber-400 grid place-items-center">
          <BellRing className="w-6 h-6" />
        </span>
        <div className="flex-1">
          <div className="font-display text-xl font-bold uppercase">Rappel quotidien</div>
          <div className="text-sm text-slate-600">Même quand l'application est fermée</div>
        </div>
        <Switch checked={r.enabled} disabled={busy} onCheckedChange={toggle} data-testid="reminder-toggle" />
      </div>
      {r.enabled && (
        <div className="flex items-center gap-3 flex-wrap">
          <label htmlFor="reminder-time" className="text-sm font-semibold text-slate-600">Heure</label>
          <Input
            id="reminder-time"
            type="time"
            value={r.time}
            onChange={(e) => changeTime(e.target.value)}
            className="h-12 w-36 text-lg font-bold"
            data-testid="reminder-time-input"
          />
          {r.push && (
            <button onClick={test} data-testid="reminder-test-btn" className="h-12 px-4 rounded-xl border-2 border-slate-300 hover:border-orange-500 font-bold flex items-center gap-2 transition-colors">
              <Send className="w-4 h-4" /> Tester
            </button>
          )}
        </div>
      )}
    </div>
  );
}
